import { toasterMsg } from '../../../../Components/Toaster/Toaster';
import t from './costingTemplate.types';
import {
  getCostingTemplateDeatailByIdService,
  addAddtionalColumnService,
  addLandedInfoColumnService,
  deleteCostingTemplateService,
  addCostingTemplateService,
  deleteCostingTemplateColumnService,
  updateAddtionalColumnService,
  updateLandedInfoColumnService,
  copyCostingTemplateDocService,
  setCTAdditionalSequence,
  setCTLandedSequence,
  getCostingTemplateListByDevOrderService,
  getCostingTemplateListService,
  setMultiDeleteLandedSequence as setMultiDeleteLandedSequenceApi,
  updateLandedInfoColumnServiceActiveInActive as updateLandedInfoColumnServiceActiveInActiveApi,
  updateCostingTemplate as updateCostingTemplateApi,
} from '../../../../services/costing-template.service';
import { fetchCustomColumnListService, fetchCustomSectionListService } from '../../../../services/customSection.service';
import { ERROR_MESSAGE } from '../../../../Components/helper';
import type {
  ApiErrorData,
  ApiResponse,
  CostingTemplateColumn,
  CostingTemplateDetail,
  CostingTemplateThunkAction,
  NamedRef,
  RemoveColumnPayload,
  SelectedColumnIds,
  TransactionMsg,
} from './costingTemplate.types';
import { HTTP_STATUS } from '../../../../constants/api-status-codes';
import i18next from 'i18next';

/*
 * Ported from Library/CostingTemplate/store/CostingTemplate.action.js.
 *
 * Seven thunks are deliberately NOT carried across, because nothing in the app calls them:
 * `getCostingTemplateDetailsByIds` (plural), `updateColumnFields`, `updateCostingTemplateName`,
 * `searchCostingColumn`, `addLandedRow`, `removeUnsavedAdditionalRow` and `removeUnsavedLandedRow`.
 * Their action types stay declared, since the reducer still handles them and the parity test drives
 * every type.
 */

const additionalColumnUpdatedSuccessfully = 'Additional column updated successfully';

/** Narrows an unknown catch variable to a display string, replacing the JavaScript's `err.toString()`. */
const toMessage = (err: unknown): string => (err instanceof Error ? err.message : String(err));

/** Reads the `errMsg` an API error envelope carries, without asserting the envelope's shape. */
const errMsgOf = (res: ApiResponse<unknown> | undefined): string | undefined => (res?.data as ApiErrorData | undefined)?.errMsg;

/** The `{ doc, totalCount }` shape the list endpoints resolve to. */
interface CostingTemplateListData {
  doc?: CostingTemplateDetail[];
  totalCount?: number;
}

/** Query params the paged list endpoint accepts. */
export interface CostingTemplateListParams {
  pageIndex?: number;
  pageSize?: number;
  searchText?: string;
  isScrolled?: boolean;
  [key: string]: unknown;
}

/* Column payloads carry `id?: string | undefined` rather than a plain optional: the id comes
   straight from `costingTemplateDetail?._id`, which is genuinely optional at every call site, and
   exactOptionalPropertyTypes rejects assigning `undefined` to a plain optional. */

/** The `{ errTemplates }` a duplicate-name save resolves to. */
interface DuplicateTemplateData {
  errTemplates?: string[];
}

/** Opens or closes the add-template modal. */
export const costingTemplateModalControl =
  (state: boolean): CostingTemplateThunkAction<Promise<void>> =>
  async (dispatch) => {
    try {
      await dispatch({ type: t.ADD_COSTING_MODAL_CONTROL, payload: state });
    } catch (err) {
      console.log(toMessage(err));
      toasterMsg(ERROR_MESSAGE, 'danger');
    }
  };

/** Creates one or more templates, then refreshes the first page. Resolves `'err'` on a name clash. */
export const addCostingTemplateAction =
  (data: unknown, transactionMsg: TransactionMsg): CostingTemplateThunkAction<Promise<boolean | 'err'>> =>
  async (dispatch) => {
    try {
      const res = (await addCostingTemplateService(data)) as ApiResponse<DuplicateTemplateData>;
      if (res?.code === 200) {
        const errTemplates = res?.data?.errTemplates;
        if (Array.isArray(errTemplates) && errTemplates.length > 0) {
          toasterMsg(`${errTemplates.join(', ')}  Exist with same name. Duplicate entries not allowed for section.`, 'danger');
          return 'err';
        }
        toasterMsg(transactionMsg('COSTING_TO_DO_LIST.TEMPLATE_ADDED'), 'success');
        await dispatch(getCostingTemplateList({ pageIndex: 1, pageSize: 20 }));
        return true;
      }
      /* The original returned a bare `false` here, so a rejected envelope left the draft row sitting
         with no feedback at all. Surfaced now that the endpoint enforces the required scoping fields
         server-side — a validation failure nothing reports is a validation failure nobody can act on. */
      const errMsg = errMsgOf(res);
      if (errMsg) toasterMsg(transactionMsg(String(errMsg)), 'danger');
      return false;
    } catch (err) {
      console.log(toMessage(err));
      toasterMsg(ERROR_MESSAGE, 'danger');
      return false;
    }
  };

/** Templates available to one development order. Consumed by Product Costing. */
export const getCostingTemplateListByDevOrder =
  (devOrderId: string | undefined): CostingTemplateThunkAction<Promise<boolean>> =>
  async (dispatch) => {
    try {
      const res = (await getCostingTemplateListByDevOrderService(devOrderId)) as ApiResponse<CostingTemplateListData>;
      if (res?.code === 200) {
        await dispatch({
          type: t.SET_COSTING_TEMPLATE_LIST,
          payload: {
            data: res?.data?.doc || [],
            totalCount: res?.data?.doc?.length || 0,
            isScrolled: false,
          },
        });
        return true;
      }
      return false;
    } catch (err) {
      console.log(toMessage(err));
      toasterMsg(ERROR_MESSAGE, 'danger');
      return false;
    }
  };

/** The paged library list. `searchText` and `isScrolled` decide replace vs append in the reducer. */
export const getCostingTemplateList =
  (params: CostingTemplateListParams): CostingTemplateThunkAction<Promise<boolean>> =>
  async (dispatch) => {
    try {
      const res = (await getCostingTemplateListService(params)) as ApiResponse<CostingTemplateListData>;
      if (res?.code === 200) {
        await dispatch({
          type: t.SET_COSTING_TEMPLATE_LIST,
          payload: {
            data: res?.data?.doc || [],
            totalCount: res?.data?.totalCount || 0,
            searchText: params.searchText as string,
            isScrolled: params.isScrolled as boolean,
          },
        });
        return true;
      }
      return false;
    } catch (err) {
      console.log(toMessage(err));
      toasterMsg(ERROR_MESSAGE, 'danger');
      return false;
    }
  };

/** One template with all four column sections. Returns the detail so callers can read it directly. */
export const getCostingTemplateDetailsById =
  (id: string | undefined): CostingTemplateThunkAction<Promise<CostingTemplateDetail | false>> =>
  async (dispatch) => {
    try {
      const res = (await getCostingTemplateDeatailByIdService(id)) as ApiResponse<CostingTemplateDetail>;
      if (res?.code === 200) {
        await dispatch({ type: t.SET_COSTING_TEMPLATE_DETAIL_BY_ID, payload: res.data as CostingTemplateDetail });
        return res.data as CostingTemplateDetail;
      }
      return false;
    } catch (err) {
      console.log(toMessage(err));
      toasterMsg(ERROR_MESSAGE, 'danger');
      return false;
    }
  };

/** Opens or closes the additional-info column modal. */
export const controlAdditionalInfoModal =
  (state: boolean): CostingTemplateThunkAction<Promise<void>> =>
  async (dispatch) => {
    try {
      await dispatch({ type: t.ADDITIONAL_MODAL, payload: state });
    } catch (err) {
      console.log(toMessage(err));
      toasterMsg(ERROR_MESSAGE, 'danger');
    }
  };

/** Opens or closes the landed-info column modal. */
export const controlLandedInfoModal =
  (state: boolean): CostingTemplateThunkAction<Promise<void>> =>
  async (dispatch) => {
    try {
      await dispatch({ type: t.LANDEDINFO_MODAL, payload: state });
    } catch (err) {
      console.log(toMessage(err));
      toasterMsg(ERROR_MESSAGE, 'danger');
    }
  };

/** Puts the row being edited into the additional-info modal, or clears it with `null`. */
export const editAdditionalInfo =
  (data: CostingTemplateColumn | null): CostingTemplateThunkAction<Promise<void>> =>
  async (dispatch) => {
    try {
      await dispatch({ type: t.EDIT_ADDITIONAL_DATA, payload: data });
    } catch (err) {
      console.log(toMessage(err));
      toasterMsg(ERROR_MESSAGE, 'danger');
    }
  };

/** Puts the row being edited into the landed-info modal, or clears it with `null`. */
export const editLandedInfo =
  (data: CostingTemplateColumn | null): CostingTemplateThunkAction<Promise<void>> =>
  async (dispatch) => {
    try {
      await dispatch({ type: t.EDIT_LANDED_DATA, payload: data });
    } catch (err) {
      console.log(toMessage(err));
      toasterMsg(ERROR_MESSAGE, 'danger');
    }
  };

/** Adds an additional-info column. Resolves `'err'` on a name clash within the section. */
export const addCostingAddtionalColumnAction =
  (obj: { id?: string | undefined; [key: string]: unknown }, transactionMsg: TransactionMsg): CostingTemplateThunkAction<Promise<boolean | 'err'>> =>
  async (dispatch) => {
    try {
      const res = (await addAddtionalColumnService(obj)) as ApiResponse<DuplicateTemplateData>;
      if (res.code === 200) {
        const errTemplates = res?.data?.errTemplates;
        if (Array.isArray(errTemplates) && errTemplates.length > 0) {
          toasterMsg(`${errTemplates.join(', ')}  Exist with same name. Duplicate entries not allowed for section.`, 'danger');
          return 'err';
        }
        toasterMsg(transactionMsg('COSTING_TO_DO_LIST.ADDITIONAL_COLUMN_ADDED'), 'success');
        await dispatch(getCostingTemplateDetailsById(obj.id));
        return true;
      }
      return false;
    } catch (err) {
      console.log(toMessage(err));
      toasterMsg(ERROR_MESSAGE, 'danger');
      return false;
    }
  };

/** Adds a landed-info column. Resolves `'err'` on a name clash within the section. */
export const addCostingLandedInfoColumnAction =
  (obj: { id?: string | undefined; [key: string]: unknown }, transactionMsg: TransactionMsg): CostingTemplateThunkAction<Promise<boolean | 'err'>> =>
  async (dispatch) => {
    try {
      const res = (await addLandedInfoColumnService(obj)) as ApiResponse<DuplicateTemplateData>;
      if (res.code === 200) {
        const errTemplates = res?.data?.errTemplates;
        if (Array.isArray(errTemplates) && errTemplates.length > 0) {
          toasterMsg(`${transactionMsg(errTemplates.join(', '))}  ${transactionMsg('COSTING_TEMPLATE_ACTION.LANDED_COLUMN_SUCCESSFULY')}`, 'danger');
          return 'err';
        }
        toasterMsg(transactionMsg('COSTING_TEMPLATE_ACTION.LANDED_COLUMN_SUCCESSFULY'), 'success');
        dispatch(getCostingTemplateDetailsById(obj.id));
        return true;
      }
      return false;
    } catch (err) {
      console.log(toMessage(err));
      toasterMsg(ERROR_MESSAGE, 'danger');
      return false;
    }
  };

/** Deletes one or more templates. `deleteId[0]` is what the reducer filters out of the list. */
export const deleteCostingTemplate =
  (deleteId: string[], transactionMsg: TransactionMsg): CostingTemplateThunkAction<Promise<boolean>> =>
  async (dispatch) => {
    try {
      const res = (await deleteCostingTemplateService({ templateIds: deleteId })) as ApiResponse<ApiErrorData>;
      if (res?.code === 200) {
        await dispatch({ type: t.REMOVE_COSTINGTEMPLATE, payload: deleteId[0] as string });
        toasterMsg(transactionMsg('COSTING_TO_DO_LIST.TEMPLATE_DELETED'), 'success');
        return true;
      }
      const errMsg = errMsgOf(res) ?? res?.message ?? ERROR_MESSAGE;
      toasterMsg(transactionMsg(errMsg) || errMsg, 'danger');
      return false;
    } catch (err) {
      /* The JavaScript reaches into the axios error for a server message before falling back; the
         shape is not typed anywhere, hence the cast rather than an interface that would imply one. */
      const axiosErr = err as { response?: { data?: { data?: ApiErrorData; message?: string } }; message?: string };
      const errMsg = axiosErr?.response?.data?.data?.errMsg ?? axiosErr?.response?.data?.message ?? axiosErr?.message ?? ERROR_MESSAGE;
      toasterMsg(transactionMsg(errMsg) || errMsg, 'danger');
      return false;
    }
  };

/** Clears the slice back to its initial state when the library screen unmounts. */
export const unmount = (): CostingTemplateThunkAction<Promise<void>> => async (dispatch) => {
  try {
    await dispatch({ type: t.COSTING_TEMPLATE_UNMOUNT });
  } catch (err) {
    console.log(toMessage(err));
    toasterMsg(ERROR_MESSAGE, 'danger');
  }
};

/** Deletes one column from one section of a template. */
export const deleteCostingTemplateColumn =
  (body: RemoveColumnPayload & { [key: string]: unknown }, transactionMsg: TransactionMsg): CostingTemplateThunkAction<Promise<boolean | undefined>> =>
  async (dispatch) => {
    try {
      const res = (await deleteCostingTemplateColumnService(body)) as ApiResponse<unknown>;
      if (res?.code === 200) {
        await dispatch({ type: t.REMOVE_COSTINGTEMPLATE_COLUMN, payload: body });
        toasterMsg(transactionMsg('COSTING_TO_DO_LIST.COLUMN_DELETED'), 'success');
        return true;
      }
      /* Falls through returning undefined on a non-200, as the original does. */
      return undefined;
    } catch (err) {
      toastRequestError(err);
      return false;
    }
  };

/** Updates an additional-info column, then closes the modal. Always resolves `'err'`, as the original does. */
export const updateCostingAddtionalColumnAction =
  (obj: { id?: string | undefined; [key: string]: unknown }, transactionMsg: TransactionMsg): CostingTemplateThunkAction<Promise<'err' | undefined>> =>
  async (dispatch) => {
    try {
      const res = (await updateAddtionalColumnService(obj)) as ApiResponse<ApiErrorData>;
      if (res.code === 200) {
        toasterMsg(additionalColumnUpdatedSuccessfully, 'success');
        dispatch(getCostingTemplateDetailsById(obj.id));
        dispatch(controlAdditionalInfoModal(false));
        dispatch(editAdditionalInfo(null));
      } else if (res.code === 500 && errMsgOf(res)) toasterMsg(transactionMsg(String(errMsgOf(res))), 'danger');
      /* Returned unconditionally in the original, including on success. Preserved: callers branch
         on it, and changing it changes what the modal does after a successful update. */
      return 'err';
    } catch (err) {
      toastRequestError(err);
      return undefined;
    }
  };

/** Updates a landed-info column, then closes the modal. */
export const updateCostingLandedColumnAction =
  (obj: { id?: string | undefined; [key: string]: unknown }): CostingTemplateThunkAction<Promise<boolean | 'err' | undefined>> =>
  async (dispatch) => {
    try {
      const res = (await updateLandedInfoColumnService(obj)) as ApiResponse<ApiErrorData>;
      if (res.code === 200) {
        toasterMsg(additionalColumnUpdatedSuccessfully, 'success');
        dispatch(getCostingTemplateDetailsById(obj.id));
        dispatch(controlLandedInfoModal(false));
        dispatch(editLandedInfo(null));
        return true;
      }
      if (res.code === 500 && errMsgOf(res)) {
        toasterMsg(String(errMsgOf(res)), 'danger');
        return 'err';
      }
      return undefined;
    } catch (err) {
      console.log(toMessage(err));
      toasterMsg(ERROR_MESSAGE, 'danger');
      return false;
    }
  };

/** Duplicates a template, then refreshes the list with the caller's current paging. */
export const copyCostingTemplateDoc =
  (id: string, params: CostingTemplateListParams, transactionMsg: TransactionMsg): CostingTemplateThunkAction<Promise<boolean>> =>
  async (dispatch) => {
    try {
      const res = (await copyCostingTemplateDocService(id)) as ApiResponse<ApiErrorData>;
      if (res?.code === 200) {
        await dispatch(getCostingTemplateList(params));
        toasterMsg(transactionMsg('COSTING_TO_DO_LIST.COPY_DOCS'), 'success');
        return true;
      }
      const resMessage = errMsgOf(res) ? transactionMsg(String(errMsgOf(res))) : ERROR_MESSAGE;
      toasterMsg(resMessage, 'danger');
      return false;
    } catch (err) {
      console.log(toMessage(err));
      toasterMsg(ERROR_MESSAGE, 'danger');
      return false;
    }
  };

/** Persists the drag order of an additional-info or additional-component section. */
export const setAdditionalSequence =
  (params: string | undefined, data: Array<{ _id: string; position: number }>, section: string): CostingTemplateThunkAction<Promise<boolean | undefined>> =>
  async (dispatch) => {
    try {
      const res = (await setCTAdditionalSequence(params, data, section)) as ApiResponse<unknown>;
      if (res?.code === 200) {
        dispatch(getCostingTemplateDetailsById(params));
        return true;
      }
      return undefined;
    } catch (error) {
      console.log(toMessage(error));
      toasterMsg(ERROR_MESSAGE, 'danger');
      return undefined;
    }
  };

/** Persists the drag order of the landed-info section. */
export const setLandedSequence =
  (params: string | undefined, data: Array<{ _id: string; position: number }>): CostingTemplateThunkAction<Promise<boolean | undefined>> =>
  async (dispatch) => {
    try {
      const res = (await setCTLandedSequence(params, data)) as ApiResponse<unknown>;
      if (res?.code === 200) {
        dispatch(getCostingTemplateDetailsById(params));
        return true;
      }
      return undefined;
    } catch (error) {
      console.log(toMessage(error));
      toasterMsg(ERROR_MESSAGE, 'danger');
      return undefined;
    }
  };

/** Bulk-deletes landed columns and resequences what remains. Passes the response straight back. */
export const setMultiDeleteLandedSequence =
  (params: string | undefined, data: unknown): CostingTemplateThunkAction<Promise<ApiResponse<unknown> | undefined>> =>
  async () => {
    try {
      return (await setMultiDeleteLandedSequenceApi(params, data)) as ApiResponse<unknown>;
    } catch (error) {
      toastRequestError(error);
      return undefined;
    }
  };

/** Columns available under one custom section, for the landed-info modal's source picker. */
export const fetchCustomColumnList =
  (sectionId: string): CostingTemplateThunkAction<Promise<ApiResponse<{ columns?: NamedRef[] }> | true>> =>
  async (dispatch) => {
    try {
      const res = (await fetchCustomColumnListService(sectionId)) as ApiResponse<{ columns?: NamedRef[] }>;
      if (res?.code === 200) {
        await dispatch({ type: t.SET_COSTING_CUSTOM_COLUMN_LIST, payload: res.data?.columns as NamedRef[] });
        return res;
      }
      if (res?.code === 500) {
        toasterMsg(String(errMsgOf(res)), 'danger');
        return res;
      }
      return res;
    } catch (err) {
      console.log(toMessage(err));
      toasterMsg(ERROR_MESSAGE, 'danger');
      return true;
    }
  };

/** The critical-path custom sections, for the landed-info modal's source picker. */
export const fetchCustomSectionList = (): CostingTemplateThunkAction<Promise<boolean | undefined>> => async (dispatch) => {
  try {
    const res = (await fetchCustomSectionListService({ type: 'critical-path' })) as ApiResponse<NamedRef[]>;
    if (res.code === 200) {
      await dispatch({ type: t.SET_COSTING_CUSTOM_SECTION_LIST, payload: res.data as NamedRef[] });
      return true;
    }
    return undefined;
  } catch (err) {
    console.log(toMessage(err));
    toasterMsg(ERROR_MESSAGE, 'danger');
    return true;
  }
};

/** Toggles a landed column's visibility, then closes the modal. */
export const updateLandedInfoColumnServiceActiveInActive =
  (obj: { id?: string | undefined; [key: string]: unknown }): CostingTemplateThunkAction<Promise<boolean | 'err' | undefined>> =>
  async (dispatch) => {
    try {
      const res = (await updateLandedInfoColumnServiceActiveInActiveApi(obj)) as ApiResponse<ApiErrorData>;
      if (res.code === 200) {
        toasterMsg(additionalColumnUpdatedSuccessfully, 'success');
        dispatch(getCostingTemplateDetailsById(obj.id));
        dispatch(controlLandedInfoModal(false));
        dispatch(editLandedInfo(null));
        return true;
      }
      if (res.code === 500 && errMsgOf(res)) {
        toasterMsg(String(errMsgOf(res)), 'danger');
        return 'err';
      }
      return undefined;
    } catch (err) {
      console.log(toMessage(err));
      toasterMsg(ERROR_MESSAGE, 'danger');
      return false;
    }
  };

/** Records which additional and landed columns are ticked for bulk delete. */
export const deleteIdsOfAdditionlaAndLanded =
  (payload: SelectedColumnIds): CostingTemplateThunkAction<Promise<boolean | undefined>> =>
  async (dispatch) => {
    try {
      await dispatch({ type: t.SET_IDS_ADDITIONAL_AND_LANDED, payload });
      return undefined;
    } catch (err) {
      console.log(toMessage(err));
      toasterMsg(ERROR_MESSAGE, 'danger');
      return false;
    }
  };

/** Appends an unsaved draft row to the additional-info section. */
export const addAdditionalRow =
  (payload: CostingTemplateColumn): CostingTemplateThunkAction<Promise<boolean | undefined>> =>
  async (dispatch) => {
    try {
      await dispatch({ type: t.ADDITIONAL_ROW, payload });
      return undefined;
    } catch (err) {
      console.log(toMessage(err));
      toasterMsg(ERROR_MESSAGE, 'danger');
      return false;
    }
  };

/** Saves every pending change to one template in a single request. */
export const updateCostingTemplate =
  (obj: { _id?: string | undefined; [key: string]: unknown }, transactionMsg: TransactionMsg): CostingTemplateThunkAction<Promise<void>> =>
  async (dispatch) => {
    try {
      const res = (await updateCostingTemplateApi(obj)) as ApiResponse<CostingTemplateDetail & ApiErrorData>;
      if (res?.code === 200) {
        await dispatch({ type: t.SET_COSTING_TEMPLATE_DETAIL_BY_ID, payload: res.data as CostingTemplateDetail });
        toasterMsg(transactionMsg('BACKEND_SERVER.CONTROLLER.LIBRARIES.COSTING_TEMPLATE_CONTROLLER.TEMPLATE_UPDATE_SUCCESSFULY'), 'success');
      } else {
        toasterMsg(transactionMsg(String(errMsgOf(res))), 'danger');
      }
    } catch (err) {
      toastRequestError(err);
    }
  };

/** The parts of a rejected axios request this module reads: the HTTP status and the envelope body. */
interface RejectedRequest {
  response?: { status?: number; data?: { message?: string; data?: ApiErrorData } };
}

/** Namespaces a server key is looked up in, in order: this module's own strings, then the shared ones. */
const SERVER_KEY_NAMESPACES = ['costing', 'common'];

/** Toasts a rejected request: the server's translated key for a 4xx, the generic message otherwise. */
function toastRequestError(err: unknown): void {
  const response = (err as RejectedRequest | undefined)?.response;
  const status = response?.status ?? 0;
  const serverKey = response?.data?.data?.errMsg ?? response?.data?.message;
  const isClientError = status >= HTTP_STATUS.BAD_REQUEST && status < HTTP_STATUS.INTERNAL_SERVER_ERROR;
  toasterMsg(isClientError && serverKey ? i18next.t(serverKey, { ns: SERVER_KEY_NAMESPACES }) : ERROR_MESSAGE, 'danger');
}
